import { useEffect, useRef, useCallback, useState } from 'react';
import { Howl } from 'howler';

/**
 * Sound effect types available in the app
 */
export type SoundEffectType = 
  | 'incomingCall'
  | 'callConnected'
  | 'callEnded'
  | 'messageReceived'
  | 'fileTransferComplete';

/**
 * Sound effect configuration
 */
interface SoundConfig {
  src: string[];
  volume: number;
  loop?: boolean;
}

/**
 * Default sound configurations - placeholder paths until samples are added
 * Sounds will be stored in /public/sounds/
 */
const SOUND_CONFIGS: Record<SoundEffectType, SoundConfig> = {
  incomingCall: {
    src: ['/sounds/incoming-call.mp3', '/sounds/incoming-call.wav'],
    volume: 0.5,
    loop: true,
  },
  callConnected: {
    src: ['/sounds/call-connected.mp3', '/sounds/call-connected.wav'],
    volume: 0.4,
    loop: false,
  },
  callEnded: {
    src: ['/sounds/call-ended.mp3', '/sounds/call-ended.wav'],
    volume: 0.4,
    loop: false,
  },
  messageReceived: {
    src: ['/sounds/message-received.mp3', '/sounds/message-received.wav'],
    volume: 0.3,
    loop: false,
  },
  fileTransferComplete: {
    src: ['/sounds/transfer-complete.mp3', '/sounds/transfer-complete.wav'],
    volume: 0.4,
    loop: false,
  },
};

/**
 * Hook for managing sound effects in the UI
 * Default: muted (sfxEnabled = false)
 * User can enable via preferences
 */
export function useSoundEffects() {
  const [sfxEnabled, setSfxEnabled] = useState(false);
  const [sfxVolume, setSfxVolume] = useState(0.5);
  const soundsRef = useRef<Map<SoundEffectType, Howl>>(new Map());
  const activeSoundsRef = useRef<Map<SoundEffectType, number>>(new Map()); // Track playing sound IDs

  // Load preferences from localStorage
  useEffect(() => {
    try {
      const enabled = localStorage.getItem('moby_pref_sfxEnabled');
      const volume = localStorage.getItem('moby_pref_sfxVolume');
      
      if (enabled !== null) {
        setSfxEnabled(JSON.parse(enabled));
      }
      if (volume !== null) {
        setSfxVolume(JSON.parse(volume));
      }
    } catch (error) {
      console.error('[SFX] Failed to load sound preferences:', error);
    }
  }, []);

  // Initialize Howl instances lazily (only when sfx is enabled)
  useEffect(() => {
    if (!sfxEnabled) {
      // Cleanup sounds when disabled
      soundsRef.current.forEach(sound => sound.unload());
      soundsRef.current.clear();
      activeSoundsRef.current.clear();
      return;
    }

    // Initialize sounds
    for (const [type, config] of Object.entries(SOUND_CONFIGS) as [SoundEffectType, SoundConfig][]) {
      if (!soundsRef.current.has(type)) {
        const sound = new Howl({
          src: config.src,
          volume: config.volume * sfxVolume,
          loop: config.loop ?? false,
          preload: false, // Don't preload until first play - saves bandwidth
          onloaderror: (_id, error) => {
            console.debug(`[SFX] Sound not loaded (expected until samples added): ${type}`, error);
          },
        });
        soundsRef.current.set(type, sound);
      }
    }

    return () => {
      soundsRef.current.forEach(sound => sound.unload());
      soundsRef.current.clear();
    };
  }, [sfxEnabled, sfxVolume]);

  // Update volume on all loaded sounds when sfxVolume changes
  useEffect(() => {
    soundsRef.current.forEach((sound, type) => {
      const config = SOUND_CONFIGS[type];
      sound.volume(config.volume * sfxVolume);
    });
  }, [sfxVolume]);

  /**
   * Play a sound effect
   */
  const play = useCallback((type: SoundEffectType): void => {
    if (!sfxEnabled) return;

    const sound = soundsRef.current.get(type);
    if (sound) {
      try {
        const id = sound.play();
        activeSoundsRef.current.set(type, id);
        console.log(`[SFX] Playing: ${type}`);
      } catch (error) {
        // Silently fail if sound file doesn't exist yet
        console.debug(`[SFX] Could not play: ${type}`, error);
      }
    }
  }, [sfxEnabled]);

  /**
   * Stop a specific sound effect (useful for looping sounds like incoming call)
   */
  const stop = useCallback((type: SoundEffectType): void => {
    const sound = soundsRef.current.get(type);
    const id = activeSoundsRef.current.get(type);
    if (sound) {
      if (id !== undefined) {
        sound.stop(id);
      } else {
        sound.stop();
      }
      activeSoundsRef.current.delete(type);
      console.log(`[SFX] Stopped: ${type}`);
    }
  }, []);

  /**
   * Stop all currently playing sounds
   */
  const stopAll = useCallback((): void => {
    soundsRef.current.forEach(sound => sound.stop());
    activeSoundsRef.current.clear();
    console.log('[SFX] Stopped all sounds');
  }, []);

  /**
   * Enable/disable sound effects
   */
  const setEnabled = useCallback((enabled: boolean): void => {
    setSfxEnabled(enabled);
    localStorage.setItem('moby_pref_sfxEnabled', JSON.stringify(enabled));
    window.dispatchEvent(new CustomEvent('user-preferences-updated', { 
      detail: { sfxEnabled: enabled } 
    }));
  }, []);

  /**
   * Set volume (0.0 to 1.0)
   */
  const setVolume = useCallback((volume: number): void => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    setSfxVolume(clampedVolume);
    localStorage.setItem('moby_pref_sfxVolume', JSON.stringify(clampedVolume));
  }, []);

  return {
    // State
    sfxEnabled,
    sfxVolume,
    
    // Actions
    play,
    stop,
    stopAll,
    setEnabled,
    setVolume,
  };
}

/**
 * Convenience hook for incoming call sound management
 * Handles the looping nature of incoming call alerts
 */
export function useIncomingCallSound() {
  const { play, stop, sfxEnabled } = useSoundEffects();
  const isPlayingRef = useRef(false);

  const startRinging = useCallback(() => {
    if (!isPlayingRef.current) {
      play('incomingCall');
      isPlayingRef.current = true;
    }
  }, [play]);

  const stopRinging = useCallback(() => {
    if (isPlayingRef.current) {
      stop('incomingCall');
      isPlayingRef.current = false;
    }
  }, [stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isPlayingRef.current) {
        stop('incomingCall');
      }
    };
  }, [stop]);

  return {
    startRinging,
    stopRinging,
    sfxEnabled,
  };
}
